<template>
  <div class="billing-address-form">
    <h5 class="adyen-checkout__fieldset__title">
      {{ t('documentsCheckout.billing_address') }}
    </h5>

    <div class="form-group dropdown-wrapper">
      <div
        data-cy="country-dropdown-selector"
        class="dropdown-trigger"
        @click="countryDropdownVisible = !countryDropdownVisible">
        <span class="label">{{ t('billingAddressForm.country') }}</span>
        <span class="value" :class="{ 'is-placeholder': !localAddress.country }">
          {{ countryDisplayName }}
        </span>
        <button class="arrow" :class="{ 'is-open': countryDropdownVisible }" type="button">
          <svg class="arrow-icon" viewBox="0 0 24 24">
            <polyline points="6 9 12 15 18 9"></polyline>
          </svg>
        </button>
      </div>
      <BackstageDropdownMenu
        data-cy="country-dropdown-menu"
        class="dropdown-menu"
        v-if="countryDropdownVisible"
        :visible="true"
        :items="countryDropdownItems"
        v-on:update:visible="countryDropdownVisible = $event"
        v-on:item-selected="selectCountry" />
    </div>

    <div class="form-row">
      <div class="form-group form-group-street">
        <BackstageInput
          :label="t('billingAddressForm.street')"
          v-model="localAddress.street"
          @blur="handleBlur('street')"
          :show-error="!isStreetValid && touched.street"
          data-cy="billing-street-input" />
        <validation-icon :is-valid="isStreetValid" :is-touched="touched.street" />
      </div>
      <div class="form-group form-group-house-number">
        <BackstageInput
          :label="t('billingAddressForm.house_number')"
          v-model="localAddress.houseNumber"
          @blur="handleBlur('houseNumber')"
          :show-error="!isHouseNumberValid && touched.houseNumber"
          data-cy="billing-house-number-input" />
        <validation-icon :is-valid="isHouseNumberValid" :is-touched="touched.houseNumber" />
      </div>
    </div>

    <div class="form-row">
      <div class="form-group form-group-postal-code">
        <BackstageInput
          :label="t('billingAddressForm.post_code')"
          v-model="localAddress.postalCode"
          @blur="handleBlur('postalCode')"
          :show-error="!isPostalCodeValid && touched.postalCode"
          data-cy="billing-postal-code-input" />
        <validation-icon :is-valid="isPostalCodeValid" :is-touched="touched.postalCode" />
      </div>
      <div class="form-group form-group-city">
        <BackstageInput
          :label="t('billingAddressForm.city')"
          v-model="localAddress.city"
          @blur="handleBlur('city')"
          :show-error="!isCityValid && touched.city"
          data-cy="billing-city-input" />
        <validation-icon :is-valid="isCityValid" :is-touched="touched.city" />
      </div>
    </div>

    <div v-show="states.length > 0" class="form-group dropdown-wrapper">
      <div
        data-cy="state-dropdown-selector"
        class="dropdown-trigger"
        @click="stateDropdownVisible = !stateDropdownVisible">
        <span class="label">{{ t('billingAddressForm.state_province') }}</span>
        <span class="value" :class="{ 'is-placeholder': !localAddress.stateOrProvince }">
          {{ stateDisplayName }}
        </span>
        <button class="arrow" :class="{ 'is-open': stateDropdownVisible }" type="button">
          <svg class="arrow-icon" viewBox="0 0 24 24">
            <polyline points="6 9 12 15 18 9"></polyline>
          </svg>
        </button>
      </div>
      <BackstageDropdownMenu
        data-cy="state-dropdown-menu"
        v-if="stateDropdownVisible"
        class="dropdown-menu"
        :visible="true"
        :items="stateDropdownItems"
        @item-selected="handleStateSelect"
        @update:visible="stateDropdownVisible = $event" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { COUNTRIES_WITH_PAYMENT_OPTION } from '@/mixins/account.mixins';
import type { DropdownItem } from '@/models/dropdown-item';
import type { AccountModel } from '@onrunning/backstage-models';
import { computed, reactive, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { countriesWithStates, type Country, type State } from '../../constants/global.constants';
import BackstageDropdownMenu from './BackstageDropdownMenu.vue';
import BackstageInput from './BackstageInput.vue';
import ValidationIcon from './ValidationIcon.vue';

export interface BillingAddress {
  street: string;
  houseNumber: string;
  city: string;
  postalCode: string;
  stateOrProvince?: string;
  country: string;
}

const props = defineProps<{
  modelValue: BillingAddress;
  selectedAccount?: AccountModel;
}>();

const emit = defineEmits<{
  'update:modelValue': [address: BillingAddress];
}>();

const { t } = useI18n();

const localAddress = reactive<BillingAddress>({ ...props.modelValue });

const touched = reactive({
  street: false,
  houseNumber: false,
  city: false,
  postalCode: false,
  stateOrProvince: false,
  country: false,
});

watch(
  () => props.modelValue,
  (newValue) => {
    Object.assign(localAddress, newValue);
  },
  { deep: true },
);

function handleBlur(field: keyof BillingAddress) {
  touched[field] = true;
  emit('update:modelValue', { ...localAddress });
}

const isStreetValid = computed(() => !!localAddress.street);
const isHouseNumberValid = computed(() => !!localAddress.houseNumber);
const isCityValid = computed(() => !!localAddress.city);
const isPostalCodeValid = computed(() => !!localAddress.postalCode);

const countriesWithPaymentOption = COUNTRIES_WITH_PAYMENT_OPTION;
const countryDropdownVisible = ref(false);
const stateDropdownVisible = ref(false);

const states = computed(() => {
  const selectedCountry = countriesWithPaymentOption.find((c) => c.code === localAddress.country);
  if (!selectedCountry) return [];
  const countryKey = selectedCountry.name.toLowerCase();

  return countriesWithStates[countryKey] || [];
});

const countryDisplayName = computed(() => {
  const selected = countriesWithPaymentOption.find((c) => c.code === localAddress.country);

  return selected ? selected.name : t('billingAddressForm.select_country');
});

const stateDisplayName = computed(() => {
  const selected = states.value.find((s) => s.code === localAddress.stateOrProvince);

  return selected ? selected.name : t('billingAddressForm.select_state_province');
});

const countryDropdownItems = computed((): DropdownItem[] => {
  return countriesWithPaymentOption.map((country) => ({
    key: country.code,
    label: country.name,
    code: country.code,
    name: country.name,
  }));
});

function selectCountry(item: DropdownItem | Country) {
  const newCountryCode = (item as DropdownItem).key || (item as Country).code;

  if (localAddress.country !== newCountryCode) {
    localAddress.stateOrProvince = '';
    touched.stateOrProvince = false;
  }
  localAddress.country = newCountryCode;
  countryDropdownVisible.value = false;

  handleBlur('country');
  if (localAddress.stateOrProvince === '') {
    handleBlur('stateOrProvince');
  }
}

const stateDropdownItems = computed((): DropdownItem[] => {
  return states.value.map((state) => ({
    key: state.code,
    label: state.name,
    code: state.code,
    name: state.name,
  }));
});

function handleStateSelect(item: DropdownItem | State) {
  localAddress.stateOrProvince = (item as DropdownItem).key || (item as State).code;
  stateDropdownVisible.value = false;
  handleBlur('stateOrProvince');
}
</script>

<style scoped lang="scss">
.form-group {
  position: relative;
  width: 100%;
  margin-bottom: 20px;

  :deep(.validation-icon-wrapper) {
    position: absolute;
    right: 14px;
    top: 46.4px;
    transform: translateY(-50%);
    z-index: 5;
    display: flex;
    align-items: center;
    justify-content: center;

    &.in-dropdown {
      right: 40px;
    }

    svg {
      width: 20px;
      height: 20px;
    }

    .icon-success {
      color: var(--color-success, green);
    }

    .icon-error {
      color: var(--color-danger, red);
    }

    .error-tooltip {
      position: absolute;
      left: 100%;
      top: 50%;
      transform: translate(10px, -50%);
      background-color: var(--color-danger, red);
      color: white;
      padding: 4px 8px;
      border-radius: 4px;
      font-size: 0.75rem;
      white-space: nowrap;
      z-index: 10;
      opacity: 0;
      visibility: hidden;
      transition:
        opacity 0.2s,
        visibility 0.2s;
    }

    &:hover .error-tooltip {
      opacity: 1;
      visibility: visible;
    }
  }
}

.adyen-checkout__fieldset__title {
  text-align: left;
  color: var(--primary-text-remark-color);
  font-weight: 500;
  font-size: 16px;
  margin-top: 2rem;
}

.form-row {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
}
.form-group-street {
  flex-basis: 60%;
  flex-grow: 2;
}
.form-group-house-number {
  flex-basis: 35%;
  flex-grow: 1;
}
.form-group-postal-code {
  flex-basis: 35%;
  flex-grow: 1;
}
.form-group-city {
  flex-basis: 60%;
  flex-grow: 2;
}

.dropdown-wrapper {
  position: relative;
  overflow: visible !important;
  z-index: 100 !important;
  cursor: pointer;
}

.dropdown-trigger {
  background: var(--bg-page-color);
  border: 1px solid var(--primary-border-color, transparent);
  border-radius: 4px;
  height: 4rem;
  padding: 10px 14px;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  position: relative;
  text-align: left;
  gap: 8px;

  .label {
    color: var(--primary-text-input-color);
    line-height: 1.125rem;
    font-size: 0.75rem;
  }

  .is-placeholder {
    color: var(--primary-text-input-color);
    opacity: 0.7;
  }

  .value {
    font-size: 0.875rem;
    font-weight: 400;
    color: var(--primary-text-color);
    margin-top: 2px;
  }

  .arrow {
    position: absolute;
    right: 14px;
    top: 70%;
    transform: translateY(-50%);
    padding: 0;
    border: none;
    background: transparent;
    transition: transform 0.3s cubic-bezier(0.25, 1, 0.5, 1);
    width: 16px;
    height: 12px;
    line-height: 12px;
    color: var(--primary-text-color);
    font-weight: bold;
    text-shadow:
      0.5px 0px 0 var(--primary-text-color),
      0px 0.5px 0 var(--primary-text-color);
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;

    .arrow-icon {
      width: 100%;
      height: 100%;
      stroke: var(--primary-text-color);
      stroke-width: 3;
      fill: none;
      stroke-linecap: round;
      stroke-linejoin: round;
    }
  }

  .arrow.is-open {
    transform: translateY(-50%) rotate(180deg);
  }
}

.dropdown-menu {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  z-index: 10;
  margin-top: -5px;
  max-height: 200px;
  overflow-y: auto;
  margin-left: 10px;
  width: 96%;
}

:deep(.dropdown-menu) {
  z-index: 2000 !important;
  background-color: var(--primary-bg-color, white);
  backdrop-filter: none;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  border-radius: 0.25rem;
  li {
    color: var(--primary-text-color, black);
    border: none !important;
    padding: 5px 16px;
    white-space: nowrap;
    text-align: left;
    font-size: 14px;
    font-weight: 0;
    line-height: 22px;
  }
}

.form-group:not(.dropdown-wrapper) {
  :deep(.field-new) {
    background: var(--bg-page-color);
    border-radius: 4px;
    height: 4rem;
    padding: 10px 14px;
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    justify-content: flex-start;

    label,
    .label {
      font-size: 0.75rem;
      color: var(--primary-text-input-color);
      line-height: 1.125rem;
      margin: 0;
      padding: 0;
      display: block;
      position: static !important;
      transform: none !important;
    }

    .input-container {
      width: 100%;
      /* MODIFICATION: Increased margin-top from 2px to 6px */
      margin-top: 6px;
      /* Give space for the icon */
      padding-right: 24px;

      input {
        padding: 0;
        border: none;
        background: transparent;
        width: 100%;
        height: auto;
        color: var(--primary-text-color);
        font-size: 0.875rem;
        font-weight: 400;

        &::placeholder {
          color: var(--primary-text-input-color);
          opacity: 1;
          font-weight: 400;
          font-size: 0.875rem;
        }
      }
    }

    &:focus-within {
      border: none;
      box-shadow: none;
    }
  }
}
</style>
